import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { defineCustomElements } from "wpp-phoenix-player/loader";
import { useMounted } from "../../hooks/useMounted";
import {
    setPersistentPlayerStatus,
    resetPersistentPlayerStatus,
} from "../../actions/PersistentPlayer/dispatcher";
import {
    PERSISTENT_PLAYER_HOME_SELECTOR,
    PERSISTENT_PLAYER_SELECTOR,
    PERSISTENT_PLAYER_PHOENIX_CONTAINER,
    PHOENIX_ELEMENT_NAME,
    KEY_CODES,
    DAI_MEDIA_TYPE,
    AUDIO_ASPECT_RATIO,
    VIDEO_ASPECT_RATIOS,
} from "./constants";
import { getPlayableMediaData } from "./utils";
import { setA11yNotificationText } from "../../reducers/A11y/reducer";
import PersistentAudioUI from "./components/PersistentUI/components/PersistentAudioUI/component";
import PersistentVideoUI from "./components/PersistentUI/components/PersistentVideoUI/component";
import { isServer } from "../../utils/environmentDetection/library";
import { PHOENIX_PLAYER_MAX_HEIGHT } from "./constants";
import { VIDEO_CANONICAL_BASE_PATH } from "../../constants/videos";
import { getIsLiveStream } from "../../utils/dataTransform/ContentItem/library";

let accessibilityDiv;
let firstFocusablePlayerElem;

const PersistentPlayer = () => {
    const phoenixInstance = useRef(null);
    const playerMarkupRef = useRef(null);
    const closeBtnRef = useRef(null);

    const persistentPlayerState = useSelector(
        (state) => state.persistentPlayer
    );

    const [isMinimized, setIsMinimized] = useState(false);
    const [playableMediaItem, setPlayableMediaItem] = useState({});
    const [isAdvertising, setIsAdvertising] = useState(false);

    const playerContainer = useRef(null);
    const phoenixLocalState = useRef({
        isInitialized: null,
        isMediaLoading: false,
        routeUpdate: false,
        featureAttribution: null,
        aspectRatio: null,
        useMediaAspectRatio: false,
        maxHeight: PHOENIX_PLAYER_MAX_HEIGHT,
        selector: PERSISTENT_PLAYER_HOME_SELECTOR,
        playingSourceId: null,
        isMediaDataLoaded: false,
    });

    const history = useHistory();
    const isMounted = useMounted();

    const dispatch = useDispatch();

    const updatePersistentPlayerState = (newStatus) =>
        dispatch(setPersistentPlayerStatus(newStatus));
    const resetPersistentPlayerState = () =>
        dispatch(resetPersistentPlayerStatus());

    const movePersistentPlayer = (selector) => {
        const playerMarkup = playerMarkupRef.current;
        const parentNode = window.document.getElementsByClassName(selector)[0];
        if (playerMarkup && parentNode && !parentNode.contains(playerMarkup)) {
            if (selector === PERSISTENT_PLAYER_HOME_SELECTOR) {
                phoenixInstance.current?.updateSizeConfig?.({
                    useMediaAspectRatio: false,
                });
            }
            parentNode.appendChild(playerMarkup);
        }
    };

    const resetPhoenixMediaData = () => {
        const { isInitialized, isMediaDataLoaded } = phoenixLocalState.current;
        const { shouldDisableAds } = persistentPlayerState.mediaDataOverrides;
        if (isInitialized && phoenixInstance.current && isMediaDataLoaded) {
            phoenixLocalState.current.isMediaDataLoaded = false;
            phoenixInstance.current.unload();
            phoenixInstance.current.setAdsInactive(shouldDisableAds);
            phoenixInstance.current.load();
        } else if (phoenixInstance.current) {
            phoenixInstance.current.setAdsInactive(shouldDisableAds);
        }
    };

    const updatePhoenixSizeConfig = () => {
        if (!phoenixLocalState.current.isInitialized) return;
        const {
            aspectRatio,
            useMediaAspectRatio,
            maxHeight,
            isMediaDataLoaded,
        } = phoenixLocalState.current;

        const _useMediaAspectRatio = isMediaDataLoaded
            ? useMediaAspectRatio
            : false;
        phoenixInstance.current?.updateSizeConfig({
            aspectRatio,
            useMediaAspectRatio: _useMediaAspectRatio,
            maxHeight,
        });
    };

    const getMediaAspectRatio = (format, aspectRatio) => {
        return format === "audio"
            ? AUDIO_ASPECT_RATIO
            : VIDEO_ASPECT_RATIOS[aspectRatio];
    };

    const setPersistentPlayerFocus = () => {
        firstFocusablePlayerElem = phoenixInstance?.current?.firstChild;
        if (!isHidden()) firstFocusablePlayerElem?.focus();
    };

    const initializePhoenixPlayer = (mediaData) => {
        phoenixLocalState.current.isInitialized = false;
        customElements.whenDefined(PHOENIX_ELEMENT_NAME).then(() => {
            const { aspectRatio, useMediaAspectRatio, maxHeight } =
                phoenixLocalState.current;

            const phoenixConfig = {
                aspectRatio,
                useMediaAspectRatio,
                enableBrowserControls: false,
                maxHeight,
                // This will need to be switched to true once the AUG payload can accept the mediaNetId
                useMediaNetIdForAds: true,
            };

            const player = document.createElement(PHOENIX_ELEMENT_NAME);

            if (mediaData && mediaData.sourceId) {
                phoenixConfig.autoPlay = true;
                player.mediaData = mediaData;
                phoenixLocalState.current.isMediaDataLoaded = true;
            }

            player.config = phoenixConfig;

            player.addEventListener("statusChanged", onPhoenixStatusChange);
            player.addEventListener("playerInitialized", onPlayerInitialized);
            player.addEventListener(
                "recommendationClicked",
                onRecommendationClick
            );
            player.dataset.testid = PHOENIX_ELEMENT_NAME;
            phoenixInstance.current = player;
            playerContainer.current.appendChild(player);
        });
    };

    // Mount
    useEffect(() => {
        if (isServer()) return;
        if (!customElements.get(PHOENIX_ELEMENT_NAME)) {
            defineCustomElements();
        }
        const isVideoDetailPage = history.location?.pathname?.startsWith(
            VIDEO_CANONICAL_BASE_PATH
        );
        if (
            !isVideoDetailPage &&
            phoenixLocalState.current.isInitialized === null
        ) {
            initializePhoenixPlayer();
        }

        accessibilityDiv = document.querySelector("a.a11y");
    }, []);

    useEffect(() => {
        setPersistentPlayerFocus();
    }, [persistentPlayerState.isVisible]);

    // Redux Persistent Player routeUpdate changed
    useEffect(() => {
        phoenixLocalState.current.routeUpdate =
            persistentPlayerState.routeUpdate;
    }, [persistentPlayerState.routeUpdate]);

    // Redux Persistent Player Media Item ID changed
    useEffect(() => {
        if (!isMounted) return;

        const { sourceId } = persistentPlayerState.mediaItem;

        // sourceId has changed, clear the mediaData in Player
        resetPhoenixMediaData();

        // if sourceId is empty, don't proceed
        // this happens when VDP clears redux state
        if (!sourceId) return;

        // save the sourceId from redux into component's local state
        phoenixLocalState.current.playingSourceId = sourceId;

        const {
            enableRecommendations = false,
            isPlayable = false,
            feature,
            ...mediaItem
        } = persistentPlayerState.mediaItem;

        let _feature = null;
        const { featureAttribution } = phoenixLocalState.current;
        if (mediaItem.sourceId && featureAttribution) {
            _feature = { ...featureAttribution };
            phoenixLocalState.current.featureAttribution = null;
        } else {
            _feature = feature ? { ...feature } : null;
        }

        // New media will be loaded asynchronously, reset Phoenix player Sizing
        const { aspectRatio } = persistentPlayerState.sizeConfig;
        const mediaAspectRatio = getMediaAspectRatio(
            mediaItem?.type,
            aspectRatio
        );
        if (mediaAspectRatio) {
            phoenixLocalState.current.aspectRatio = mediaAspectRatio;
        }
        updatePhoenixSizeConfig();

        getPlayableMediaData(mediaItem, !isPlayable).then((playableMedia) => {
            let _playableMediaItem = playableMedia
                ? {
                      ...playableMedia,
                      enableRecommendations,
                      feature: _feature,
                  }
                : {};
            setPlayableMediaItem(_playableMediaItem);

            // persistent player relies on sourceId and we don't
            // even load the media if it's not present
            if (!_playableMediaItem.sourceId) return;

            // track that new media is loading in local state
            phoenixLocalState.current.isMediaLoading = true;

            phoenixLocalState.current.aspectRatio = getMediaAspectRatio(
                _playableMediaItem.format,
                aspectRatio ?? _playableMediaItem.aspectRatio
            );

            if (_playableMediaItem.format === "video") {
                phoenixLocalState.current.useMediaAspectRatio =
                    persistentPlayerState.sizeConfig.useMediaAspectRatio ??
                    false;

                const thumbnail =
                    persistentPlayerState.mediaDataOverrides?.thumbnail;
                if (thumbnail)
                    _playableMediaItem = {
                        ..._playableMediaItem,
                        thumbnail: thumbnail,
                    };
            } else {
                phoenixLocalState.current.useMediaAspectRatio = false;
            }

            phoenixLocalState.current.maxHeight =
                persistentPlayerState.sizeConfig.maxHeight ??
                PHOENIX_PLAYER_MAX_HEIGHT;

            _playableMediaItem = {
                ..._playableMediaItem,
                playNextActive: mediaItem.playNextActive ?? false,
            };

            if (phoenixLocalState.current.isInitialized === null) {
                initializePhoenixPlayer(_playableMediaItem);
            } else {
                phoenixInstance.current.mediaData = _playableMediaItem;
                phoenixLocalState.current.isMediaDataLoaded = true;
            }

            // Update the controls config
            const { hideFullscreenBtn } = persistentPlayerState.controlsConfig;
            phoenixInstance?.current?.updateControlsConfig({
                hideFullscreenBtn: hideFullscreenBtn ?? false,
            });
        });
    }, [persistentPlayerState.mediaItem.sourceId]);

    // Redux Persistent Player Selector changed
    useEffect(() => {
        const toSelector = persistentPlayerState.selector;
        const isPersistentSelector =
            toSelector === PERSISTENT_PLAYER_HOME_SELECTOR;

        const { selector, isMediaDataLoaded, playingSourceId } =
            phoenixLocalState.current;

        if (selector === toSelector) return;
        phoenixLocalState.current.selector = toSelector;

        const { isVisible, status, forcePersistence, mediaItem } =
            persistentPlayerState;

        // check if playing media is a DAI stream
        const isDaiPlaying =
            phoenixInstance.current?.mediaData?.assets?.[0]?.type ===
            DAI_MEDIA_TYPE;

        // if isVisible is false, player should not persist
        let doNotPersist = !isVisible;

        // if will move to persistent state, should be visible and forcePersistence is not
        if (isPersistentSelector && isVisible && !forcePersistence) {
            if (isAdvertising) {
                // if playing ads, we only persist DAI streams
                doNotPersist = !isDaiPlaying;
            } else if (status === "error") {
                // if status is error, we only persist Live Streams (as it might be upcoming)
                const isLive = getIsLiveStream(mediaItem?.media?.streamType);
                doNotPersist = !isLive;
            } else {
                // lastly, do not persist if not currently playing
                doNotPersist = status !== "playing";
            }
        }

        // Moving the player in the DOM always breaks the IMA Ad container.
        // By stopping the playback now and calling play again after moving,
        // we force the player to reload Ads module.
        let didCallStop = false;
        if ((isDaiPlaying || isAdvertising) && isMediaDataLoaded) {
            phoenixInstance.current.stop();
            didCallStop = true;
        }

        // if the player is moving out of persistent state, force a resize to
        // fix issues with vertical videos
        if (!isPersistentSelector) {
            if (mediaItem?.sourceId === playingSourceId) {
                phoenixLocalState.current.useMediaAspectRatio =
                    persistentPlayerState.sizeConfig.useMediaAspectRatio;
            } else {
                phoenixLocalState.current.useMediaAspectRatio = false;
                phoenixLocalState.current.maxHeight = null;
            }
            updatePhoenixSizeConfig();
        }

        movePersistentPlayer(toSelector);
        setPersistentPlayerFocus();

        // if doNotPersist is true, we reset the player, the redux state, and return
        if (doNotPersist) {
            resetPersistentPlayerState();
            return;
        }

        if (didCallStop) {
            phoenixInstance.current.play();
        }
    }, [persistentPlayerState.selector]);

    const onRecommendationClick = async ({ detail }) => {
        const { mediaId, sourceId, source, featureAttribution } = detail;
        const mpxId = parseInt(mediaId);

        if (phoenixLocalState.current.routeUpdate) {
            phoenixLocalState.current.featureAttribution = {
                ...featureAttribution,
            };
            history.push(
                `${VIDEO_CANONICAL_BASE_PATH}video/${sourceId ?? mpxId}`
            );
        } else {
            updatePersistentPlayerState({
                mediaItem: {
                    sourceId: sourceId ?? mediaId,
                    mediaId: mpxId,
                    source: source,
                    type: "video",
                    isPlayable: false,
                    enableRecommendations: true,
                    feature: featureAttribution,
                    playNextActive: detail.playNextActive ?? false,
                },
            });
        }
    };

    const onPhoenixStatusChange = ({ detail }) => {
        if (detail.to === "PLAYING") {
            dispatch(
                setA11yNotificationText(
                    "Video will keep playing when you leave this page"
                )
            );
        }
        if (detail.from === "PLAYING") {
            dispatch(setA11yNotificationText(""));
        }
        const _status = detail.to?.toLowerCase?.();
        if (!_status) return;

        setIsAdvertising(_status.startsWith("adver"));

        updatePersistentPlayerState({ status: _status });
        const { isMediaLoading, selector } = phoenixLocalState.current;
        if (isMediaLoading && detail.to === "PLAYING") {
            phoenixLocalState.current.isMediaLoading = false;
            if (selector !== PERSISTENT_PLAYER_HOME_SELECTOR)
                updatePhoenixSizeConfig();
        }
    };

    const onPlayerInitialized = ({ detail }) => {
        phoenixLocalState.current.isInitialized = detail;
    };

    const isHidden = () => {
        if (persistentPlayerState.selector !== PERSISTENT_PLAYER_HOME_SELECTOR)
            return "pp-hidden";
        return persistentPlayerState.isVisible ? "" : "pp-hidden";
    };

    const addErrorClass = () => {
        return persistentPlayerState.status === "error" ? " error" : "";
    };

    const addAdvertisingClass = () => {
        return isAdvertising ? " advertising" : "";
    };

    const onCloseClick = (e) => {
        e.preventDefault();
        resetPhoenixMediaData();
        resetPersistentPlayerState();
        dispatch(setA11yNotificationText(""));
        accessibilityDiv?.focus();
    };

    const onKeyDown = (event) => {
        switch (event.code) {
            case KEY_CODES.TAB:
                if (
                    event.shiftKey &&
                    document.activeElement === firstFocusablePlayerElem
                ) {
                    event.preventDefault();
                    accessibilityDiv?.focus();
                } else if (
                    !event.shiftKey &&
                    document.activeElement === closeBtnRef.current
                ) {
                    event.preventDefault();
                    accessibilityDiv?.focus();
                }
                break;
            case KEY_CODES.ESCAPE:
                event.preventDefault();
                onCloseClick(event);
                break;
            default:
                break;
        }
    };

    let mediaClass = "video";
    let labelledBy = "persistent-player-media-metadata-video";
    let PersistentUI;

    if (persistentPlayerState.mediaItem.type === "audio") {
        mediaClass = "audio";
        labelledBy = "media-metadata-audio";
        PersistentUI = (
            <PersistentAudioUI
                {...{
                    onCloseClick,
                    isMinimized,
                    setIsMinimized: (isMin) => {
                        setIsMinimized(isMin);
                    },
                }}
                ref={closeBtnRef}
            />
        );
    } else {
        if (isMinimized) setIsMinimized(false);
        PersistentUI = (
            <PersistentVideoUI
                {...{
                    onCloseClick,
                    mediaItem: playableMediaItem,
                }}
            />
        );
    }

    return (
        <div
            className={`persistent-player-persist-container ${isHidden()} ${mediaClass} ${
                isMinimized ? "minimized" : ""
            }`}
            id="persistent-player"
            tabIndex="-1"
            aria-labelledby={labelledBy}
            data-testid="persistent-player-persist-container"
        >
            <div
                className={`${PERSISTENT_PLAYER_HOME_SELECTOR}`}
                data-testid={`${PERSISTENT_PLAYER_HOME_SELECTOR}`}
                tabIndex="-1"
                role="tab"
                onKeyDown={onKeyDown}
            >
                <div
                    className={`${PERSISTENT_PLAYER_SELECTOR} ${mediaClass}${addErrorClass()}${addAdvertisingClass()}`}
                    data-testid={`${PERSISTENT_PLAYER_SELECTOR}`}
                    ref={playerMarkupRef}
                >
                    <div
                        className={`${PERSISTENT_PLAYER_PHOENIX_CONTAINER}`}
                        data-testid={`${PERSISTENT_PLAYER_PHOENIX_CONTAINER}`}
                        ref={playerContainer}
                    ></div>
                </div>
                {PersistentUI}
            </div>
        </div>
    );
};

export default PersistentPlayer;
